/**
 * Master sport catalog for groups. Only some sports use belt+stripe (hasBeltRank).
 */

export const LEGACY_BELT_SYSTEM_DEFAULT = "bjj";

/** @type {{ key: string; labelKey: string; hasBeltRank: boolean; maxStripes?: number }[]} */
export const MASTER_SPORT_CATALOG = [
  { key: "bjj", labelKey: "sport_key_bjj", hasBeltRank: true, maxStripes: 4 },
  { key: "judo", labelKey: "sport_key_judo", hasBeltRank: true, maxStripes: 0 },
  { key: "karate", labelKey: "sport_key_karate", hasBeltRank: true, maxStripes: 0 },
  { key: "taekwondo", labelKey: "sport_key_taekwondo", hasBeltRank: true, maxStripes: 0 },
  { key: "aikido", labelKey: "sport_key_aikido", hasBeltRank: true, maxStripes: 0 },
  { key: "hapkido", labelKey: "sport_key_hapkido", hasBeltRank: true, maxStripes: 0 },
  { key: "sambo", labelKey: "sport_key_sambo", hasBeltRank: true, maxStripes: 0 },
  { key: "kung_fu", labelKey: "sport_key_kung_fu", hasBeltRank: true, maxStripes: 0 },
  { key: "kendo", labelKey: "sport_key_kendo", hasBeltRank: true, maxStripes: 0 },
  { key: "jujutsu", labelKey: "sport_key_jujutsu", hasBeltRank: true, maxStripes: 4 },
  { key: "savate", labelKey: "sport_key_savate", hasBeltRank: true, maxStripes: 0 },
  { key: "krav_maga", labelKey: "sport_key_krav_maga", hasBeltRank: true, maxStripes: 0 },
  { key: "capoeira", labelKey: "sport_key_capoeira", hasBeltRank: true, maxStripes: 0 },
  { key: "nogi", labelKey: "sport_key_nogi", hasBeltRank: false },
  { key: "wrestling", labelKey: "sport_key_wrestling", hasBeltRank: false },
  { key: "thaiboxing", labelKey: "sport_key_thaiboxing", hasBeltRank: false },
  { key: "muay_thai", labelKey: "sport_key_muay_thai", hasBeltRank: false },
  { key: "boxing", labelKey: "sport_key_boxing", hasBeltRank: false },
  { key: "kickboxing", labelKey: "sport_key_kickboxing", hasBeltRank: false },
  { key: "mma", labelKey: "sport_key_mma", hasBeltRank: false },
];

const CATALOG_BY_KEY = new Map(MASTER_SPORT_CATALOG.map((s) => [s.key, s]));

/** Common aliases (e.g. Swedish labels stored as keys) → catalog keys */
const SPORT_KEY_ALIASES = {
  boxning: "boxing",
  thaiboxning: "thaiboxing",
  kickboxning: "kickboxing",
  muaythai: "muay_thai",
  "muay thai": "muay_thai",
};

/**
 * @param {string|null|undefined} raw
 * @returns {string|null} canonical catalog key when known
 */
export function normalizeMemberSportKey(raw) {
  if (raw == null || raw === "") return null;
  let key = String(raw).toLowerCase().trim();
  if (!key) return null;
  if (SPORT_KEY_ALIASES[key]) key = SPORT_KEY_ALIASES[key];
  return CATALOG_BY_KEY.has(key) ? key : key;
}

export const DEFAULT_SELECTABLE_SPORT_KEYS = ["bjj"];

/**
 * @param {unknown} raw
 * @returns {string[]}
 */
export function normalizeSelectableSportKeys(raw) {
  if (raw == null || !Array.isArray(raw) || raw.length === 0) {
    return [...DEFAULT_SELECTABLE_SPORT_KEYS];
  }
  const out = [];
  for (const item of raw) {
    const k = String(item || "").toLowerCase().trim();
    if (k && CATALOG_BY_KEY.has(k) && !out.includes(k)) out.push(k);
  }
  return out.length ? out : [...DEFAULT_SELECTABLE_SPORT_KEYS];
}

/**
 * @param {string[]} selectableKeys
 * @returns {{ value: string; labelKey: string; hasBeltRank: boolean }[]}
 */
export function buildGroupSportSelectOptions(selectableKeys) {
  const keys = normalizeSelectableSportKeys(selectableKeys);
  const options = [
    { value: "", labelKey: "sport_group_legacy_default", hasBeltRank: false },
  ];
  for (const key of keys) {
    const row = CATALOG_BY_KEY.get(key);
    if (row) {
      options.push({ value: row.key, labelKey: row.labelKey, hasBeltRank: row.hasBeltRank });
    }
  }
  return options;
}

/**
 * @param {string|null|undefined} formValue
 * @returns {string|null}
 */
export function sportKeyForApi(formValue) {
  if (formValue == null || formValue === "") return null;
  return String(formValue).toLowerCase().trim();
}

/**
 * @param {object|null|undefined} group
 * @returns {string}
 */
export function sportKeyFromGroup(group) {
  const sk = group?.sportKey ?? group?.sport_key;
  if (sk != null && sk !== "") return String(sk);
  const bk = group?.beltSystemKey ?? group?.belt_system_key;
  if (bk === "bjj") return "bjj";
  return "";
}

/** @deprecated use sportKeyForApi */
export function beltSystemKeyForApi(formValue) {
  return sportKeyForApi(formValue);
}

/** @deprecated use sportKeyFromGroup */
export function beltSystemKeyFromGroup(group) {
  return sportKeyFromGroup(group);
}

/**
 * Belt validation system for promotions when context comes from a training group row.
 * @param {object|null|undefined} group
 * @returns {string}
 */
export function validationBeltSystemKeyFromGroup(group) {
  const sk = sportKeyFromGroup(group);
  if (!sk) return LEGACY_BELT_SYSTEM_DEFAULT;
  const row = CATALOG_BY_KEY.get(String(sk).toLowerCase());
  if (row?.hasBeltRank) return String(sk).toLowerCase();
  return LEGACY_BELT_SYSTEM_DEFAULT;
}

/**
 * Max stripes on the same belt color (IBJJF-style). 0 = rank is belt color only.
 * @param {string|null|undefined} beltSystemKey
 */
export function getMaxStripesForBeltSystem(beltSystemKey) {
  const key = (beltSystemKey || LEGACY_BELT_SYSTEM_DEFAULT).toLowerCase();
  const row = CATALOG_BY_KEY.get(key);
  if (!row?.hasBeltRank) return 0;
  return typeof row.maxStripes === "number" ? row.maxStripes : 0;
}

export function sportUsesStripeRank(beltSystemKey) {
  return getMaxStripesForBeltSystem(beltSystemKey) > 0;
}

/**
 * @param {string|null|undefined} sportKey
 * @returns {boolean}
 */
export function sportHasBeltRank(sportKey) {
  const key = normalizeMemberSportKey(sportKey);
  if (!key) return false;
  const row = CATALOG_BY_KEY.get(key);
  return Boolean(row?.hasBeltRank);
}

/**
 * Signup sport picker options for a club (no legacy empty option).
 * @param {string[]|null|undefined} selectableKeys
 */
export function buildSignupSportSelectOptions(selectableKeys) {
  const keys = normalizeSelectableSportKeys(selectableKeys);
  return keys
    .map((key) => {
      const row = CATALOG_BY_KEY.get(key);
      if (!row) return null;
      return { value: row.key, labelKey: row.labelKey, hasBeltRank: row.hasBeltRank };
    })
    .filter(Boolean);
}

/** Stripe values for promotion pickers (always includes 0). */
export function getStripePickerValues(beltSystemKey) {
  const max = getMaxStripesForBeltSystem(beltSystemKey);
  if (max <= 0) return [0];
  return Array.from({ length: max + 1 }, (_, i) => i);
}

/** @deprecated use buildGroupSportSelectOptions */
export const GROUP_BELT_SYSTEM_OPTIONS = [
  { value: "", labelKey: "sport_group_legacy_default" },
  { value: "bjj", labelKey: "sport_key_bjj" },
];
